import { NumericSeries } from "../series/numeric-series";
import type { DerivedChannels } from "./derived-channels";
import type {
  BarWidthValue,
  OhlcValue,
  PointValue,
  RangeYValue,
  RecordValue,
} from "./payload-shape";
import type { SeriesDescriptor } from "./series-descriptor";

/** Named, heterogeneous collection of plot-ready series. */
export class PlotCollection {
  private constructor(private readonly entriesInner: ReadonlyMap<string, PlotCollection.AnyEntry>) {}

  static fromEntries(entries: readonly PlotCollection.AnyEntry[]): PlotCollection {
    const map = new Map<string, PlotCollection.AnyEntry>();
    for (const entry of entries) {
      map.set(entry.descriptor.key, entry);
    }
    return new PlotCollection(map);
  }

  get(key: string): Option<PlotCollection.AnyEntry> {
    return this.entriesInner.get(key) ?? null;
  }

  getScalar(key: string): Option<PlotCollection.ScalarEntry> {
    const entry = this.get(key);
    if (entry === null || entry === undefined) return null;
    if (!PlotCollection.isScalarEntry(entry)) return null;
    return entry;
  }

  getBarWidth(key: string): Option<PlotCollection.BarWidthEntry> {
    const entry = this.get(key);
    if (entry === null || entry === undefined) return null;
    if (!PlotCollection.isBarWidthEntry(entry)) return null;
    return entry;
  }

  getRangeY(key: string): Option<PlotCollection.RangeYEntry> {
    const entry = this.get(key);
    if (entry === null || entry === undefined) return null;
    if (!PlotCollection.isRangeYEntry(entry)) return null;
    return entry;
  }

  getOhlc(key: string): Option<PlotCollection.OhlcEntry> {
    const entry = this.get(key);
    if (entry === null || entry === undefined) return null;
    if (!PlotCollection.isOhlcEntry(entry)) return null;
    return entry;
  }

  getPoint(key: string): Option<PlotCollection.PointEntry> {
    const entry = this.get(key);
    if (entry === null || entry === undefined) return null;
    if (!PlotCollection.isPointEntry(entry)) return null;
    return entry;
  }

  getRecord(key: string): Option<PlotCollection.RecordEntry> {
    const entry = this.get(key);
    if (entry === null || entry === undefined) return null;
    if (!PlotCollection.isRecordEntry(entry)) return null;
    return entry;
  }

  keys(): readonly string[] {
    return Array.from(this.entriesInner.keys());
  }

  entries(): readonly PlotCollection.AnyEntry[] {
    return Array.from(this.entriesInner.values());
  }

  range(start: number, end: number): PlotCollection {
    const ranged: PlotCollection.AnyEntry[] = [];

    for (const entry of this.entriesInner.values()) {
      ranged.push(PlotCollection.rangeEntry(entry, start, end));
    }

    return PlotCollection.fromEntries(ranged);
  }
}

export namespace PlotCollection {
  export type ScalarEntry = Readonly<{
    descriptor: SeriesDescriptor<"scalar">;
    series: NumericSeries<number>;
    derived: Option<DerivedChannels>;
  }>;

  export type BarWidthEntry = Readonly<{
    descriptor: SeriesDescriptor<"bar-width">;
    series: NumericSeries<BarWidthValue>;
    derived: Option<DerivedChannels>;
  }>;

  export type RangeYEntry = Readonly<{
    descriptor: SeriesDescriptor<"range-y">;
    series: NumericSeries<RangeYValue>;
    derived: Option<DerivedChannels>;
  }>;

  export type OhlcEntry = Readonly<{
    descriptor: SeriesDescriptor<"ohlc">;
    series: NumericSeries<OhlcValue>;
    derived: Option<DerivedChannels>;
  }>;

  export type PointEntry = Readonly<{
    descriptor: SeriesDescriptor<"point">;
    series: NumericSeries<PointValue>;
    derived: Option<DerivedChannels>;
  }>;

  export type RecordEntry = Readonly<{
    descriptor: SeriesDescriptor<"record">;
    series: NumericSeries<RecordValue>;
    derived: Option<DerivedChannels>;
  }>;

  export type AnyEntry =
    | ScalarEntry
    | BarWidthEntry
    | RangeYEntry
    | OhlcEntry
    | PointEntry
    | RecordEntry;

  export function isScalarEntry(entry: AnyEntry): entry is ScalarEntry {
    return entry.descriptor.shape.kind === "scalar";
  }

  export function isBarWidthEntry(entry: AnyEntry): entry is BarWidthEntry {
    return entry.descriptor.shape.kind === "bar-width";
  }

  export function isRangeYEntry(entry: AnyEntry): entry is RangeYEntry {
    return entry.descriptor.shape.kind === "range-y";
  }

  export function isOhlcEntry(entry: AnyEntry): entry is OhlcEntry {
    return entry.descriptor.shape.kind === "ohlc";
  }

  export function isPointEntry(entry: AnyEntry): entry is PointEntry {
    return entry.descriptor.shape.kind === "point";
  }

  export function isRecordEntry(entry: AnyEntry): entry is RecordEntry {
    return entry.descriptor.shape.kind === "record";
  }

  export function rangeEntry(
    entry: AnyEntry,
    start: number,
    end: number,
  ): AnyEntry {
    if (isScalarEntry(entry)) {
      return {
        descriptor: entry.descriptor,
        series: entry.series.range(start, end),
        derived: entry.derived,
      };
    }
    if (isBarWidthEntry(entry)) {
      return {
        descriptor: entry.descriptor,
        series: entry.series.range(start, end),
        derived: entry.derived,
      };
    }
    if (isRangeYEntry(entry)) {
      return {
        descriptor: entry.descriptor,
        series: entry.series.range(start, end),
        derived: entry.derived,
      };
    }
    if (isOhlcEntry(entry)) {
      return {
        descriptor: entry.descriptor,
        series: entry.series.range(start, end),
        derived: entry.derived,
      };
    }
    if (isPointEntry(entry)) {
      return {
        descriptor: entry.descriptor,
        series: entry.series.range(start, end),
        derived: entry.derived,
      };
    }
    return {
      descriptor: entry.descriptor,
      series: entry.series.range(start, end),
      derived: entry.derived,
    };
  }

  export function makeScalar(
    descriptor: SeriesDescriptor<"scalar">,
    points: readonly (readonly [number, number])[],
    derived: Option<DerivedChannels> = null,
  ): ScalarEntry {
    return {
      descriptor,
      series: NumericSeries.fromPoints(points),
      derived,
    };
  }

  export function makeBarWidth(
    descriptor: SeriesDescriptor<"bar-width">,
    points: readonly (readonly [number, BarWidthValue])[],
    derived: Option<DerivedChannels> = null,
  ): BarWidthEntry {
    return {
      descriptor,
      series: NumericSeries.fromPoints(points),
      derived,
    };
  }

  export function makeRangeY(
    descriptor: SeriesDescriptor<"range-y">,
    points: readonly (readonly [number, RangeYValue])[],
    derived: Option<DerivedChannels> = null,
  ): RangeYEntry {
    return {
      descriptor,
      series: NumericSeries.fromPoints(points),
      derived,
    };
  }

  export function makeOhlc(
    descriptor: SeriesDescriptor<"ohlc">,
    points: readonly (readonly [number, OhlcValue])[],
    derived: Option<DerivedChannels> = null,
  ): OhlcEntry {
    return {
      descriptor,
      series: NumericSeries.fromPoints(points),
      derived,
    };
  }

  export function makePoint(
    descriptor: SeriesDescriptor<"point">,
    points: readonly (readonly [number, PointValue])[],
    derived: Option<DerivedChannels> = null,
  ): PointEntry {
    return {
      descriptor,
      series: NumericSeries.fromPoints(points),
      derived,
    };
  }

  export function makeRecord(
    descriptor: SeriesDescriptor<"record">,
    points: readonly (readonly [number, RecordValue])[],
    derived: Option<DerivedChannels> = null,
  ): RecordEntry {
    return {
      descriptor,
      series: NumericSeries.fromPoints(points),
      derived,
    };
  }
}
